import { Component, inject, OnInit } from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { Router } from '@angular/router';
import { Users } from '../../../models/users';
import { jwtDecode } from 'jwt-decode';
import { CognitoToken } from '../../../models/CognitoToken';
import { ApiService } from '../../../services/api.service';
import { ApiUrls } from '../../../constants/api-urls';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-auth-callback',
  standalone: false,
  templateUrl: './auth-callback.component.html',
  styleUrl: './auth-callback.component.scss',
})
export class AuthCallbackComponent implements OnInit {
  userData?: Users;
  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private didHandleAuth = false;
  private readonly profileFetchInFlightKey = 'icip_user_profile_fetch_in_flight';

  /**
   * Clears cookies set by other FirstOrion products (e.g., Engage) that share the
   * .firstorion.com domain. These stale cookies inflate request headers beyond
   * Tomcat's 8KB limit, causing 400 Bad Request errors.
   */
  private clearStaleCookies(): void {
    const icipCookieNames = new Set(['_oidc', 'angular-auth-oidc-client']);
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      if (!name) return;
      const isOidc = icipCookieNames.has(name) ||
        Array.from(icipCookieNames).some((prefix) => name.startsWith(prefix));
      if (!isOidc) {
        // Delete on current domain and parent domains
        const domains = [
          '',
          window.location.hostname,
          '.firstorion.com',
          'local-portal-eu.firstorion.com',
        ];
        domains.forEach((domain) => {
          const domainPart = domain ? `; domain=${domain}` : '';
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
        });
      }
    });
  }

  ngOnInit() {
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        if (this.didHandleAuth) {
          return;
        }
        this.didHandleAuth = true;
        this.authService.getAccessToken().subscribe((token) => {
          try {
            const decoded = jwtDecode<CognitoToken>(token);
            // console.log('Decoded token:', decoded);
            const groups = decoded['cognito:groups'];
            localStorage.clear();
            this.clearStaleCookies();
            if (groups?.includes('admin')) {
              this.router.navigate(['/admin/dashboard'], { replaceUrl: true });
            } else if (groups?.includes('reseller')) {
              this.router.navigate(['/partner/dashboard'], { replaceUrl: true });
            } else {
              const inFlight = sessionStorage.getItem(this.profileFetchInFlightKey) === '1';
              if (inFlight) {
                return;
              }
              sessionStorage.setItem(this.profileFetchInFlightKey, '1');
              this.apiService
                .get(ApiUrls.User.GET_PROFILE)
                .pipe(finalize(() => sessionStorage.removeItem(this.profileFetchInFlightKey)))
                .subscribe((response) => {
                  if (response.success) {
                    localStorage.setItem('user', JSON.stringify({ ...response.data }));
                    if (response.data.isBusinessAvailable) {
                      const hasPendingPlan = !!sessionStorage.getItem('icip_selected_plan');
                      this.router.navigate(
                        [hasPendingPlan ? '/user/checkout' : '/user/dashboard'],
                        { replaceUrl: true }
                      );
                    } else {
                      this.router.navigate(['/user/verify-business'], {
                        replaceUrl: true,
                      });
                    }
                  } else {
                    this.router.navigate(['/'], { replaceUrl: true });
                  }
                });
            }
          } catch (err) {
            this.router.navigate(['/'], { replaceUrl: true });
            console.error('Failed to decode token:', err);
          }
        });
      }
    });
  }
}
